import S, { DataSignal } from 's-js';
import { SArray as ArraySignal } from 's-array';

type id = string | number;

export type UniqueIndex<T> = (id : id | (() => id)) => () => T | null;

const UNSET = {};

export default function uniqueIndex<T>(array : ArraySignal<T>, keyFn : (v : T) => id) : UniqueIndex<T> {
    var index = {} as { [id : string ] : () => T | null },
        // how many items have this key.  must be 0 or 1 at end of update
        slotCounts = {} as { [id : string] : number },
        // change data: which keys, items and how many are changing
        changeKeys = [] as id[],
        changeItems = [] as T[], // parallel to changeKeys
        changeCount = 0,
        incKey = function (item : T, key : id) { 
            if (key in slotCounts) slotCounts[key]++ 
            else slotCounts[key] = 1;
            changeKeys[changeCount] = key;
            changeItems[changeCount] = item;
            changeCount++;
        },
        decKey = function (item : T, key : id) { 
            slotCounts[key]--; 
            changeKeys[changeCount] = key; 
            changeItems[changeCount] = UNSET as T; 
            changeCount++; 
        },
        changeBuilder = array.map<id>(
            function (item, oldkey) {
                var key = keyFn(item);
                if (key !== oldkey) {
                    if (oldkey !== undefined) decKey(item, oldkey);
                    incKey(item, key);
                }
                return key;
            },
            decKey
        ),
        updater = S.on(changeBuilder, function () {
            var errors = "";

            while (changeCount) {
                changeCount--;
                var key = changeKeys[changeCount],
                    item = changeItems[changeCount],
                    slotCount = slotCounts[key],
                    _slot = slot(key);
                
                changeItems[changeCount] = UNSET as T; // memory safe: remove ref to item

                if (slotCount === 0) {
                    _slot(null);
                } else if (slotCount === 1) { 
                    if (item !== UNSET) _slot(item);
                } else {
                    if (item !== UNSET) errors += "key: " + key + ", item: " + item + ". ";
                    slotCounts[key] = 1; // restore sane state
                }
            }
            
            if (errors) throw new Error("UniqueIndex collision(s): " + errors);
        });

    return indexRef;
    
    function indexRef(id : id | (() => id)) : () => T | null {
        return typeof id === 'function'
            ? function () {
                return slot(id())();
            }
            : slot(id);
    }

    function slot(id : id) : DataSignal<T | null> {
        return id in index ? index[id] : index[id] = S.data(null);
    }
}


// WEBPACK FOOTER //
// ./lib/uniqueIndex.ts